import { useEffect, useId, useRef, useState } from 'react';
import { FINANCE_GLOSSARY, GlossaryKey } from './glossary';

interface InfoTipProps {
  glossaryKey: GlossaryKey;
  /** Extra class on the wrapper (layout only). */
  className?: string;
}

/**
 * Spec 053 BR-31 / AC-16b — the ⓘ beside every financial figure.
 *
 * Accessible tooltip: the button is always `aria-describedby` the (visually hidden when closed)
 * explanation, so screen readers get the text without hovering. Opens on hover and keyboard focus,
 * toggles on click for touch, closes on Escape / blur / outside click. Text comes only from the
 * glossary — no ad-hoc copy.
 */
export default function InfoTip({ glossaryKey, className }: InfoTipProps) {
  const entry = FINANCE_GLOSSARY[glossaryKey];
  const tipId = useId();
  const [open, setOpen] = useState(false);
  const [pinned, setPinned] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!pinned) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setPinned(false);
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [pinned]);

  const visible = open || pinned;

  return (
    <span
      ref={wrapperRef}
      className={className}
      style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', marginLeft: '0.35rem', verticalAlign: 'middle' }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label={`About ${entry.label}`}
        aria-describedby={tipId}
        aria-expanded={visible}
        data-glossary-key={glossaryKey}
        onClick={() => setPinned((p) => !p)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') { setOpen(false); setPinned(false); }
        }}
        style={{
          background: 'none', border: '1px solid var(--admin-border, #e2e8f0)', borderRadius: '50%',
          width: '16px', height: '16px', fontSize: '0.625rem', fontWeight: 700, lineHeight: 1,
          color: 'var(--admin-text-muted, #64748b)', cursor: 'help', display: 'inline-flex',
          alignItems: 'center', justifyContent: 'center', padding: 0, fontFamily: 'inherit',
        }}
      >
        i
      </button>
      <span
        id={tipId}
        role="tooltip"
        hidden={!visible}
        style={visible ? {
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 20, width: '300px',
          background: 'var(--admin-card-bg, #fff)', border: '1px solid var(--admin-border, #e2e8f0)',
          borderRadius: '6px', padding: '0.75rem', boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          fontSize: '0.75rem', lineHeight: 1.5, color: 'var(--admin-text, #1e293b)',
          fontWeight: 400, textAlign: 'left', whiteSpace: 'normal', display: 'block',
        } : undefined}
      >
        <strong style={{ display: 'block', marginBottom: '0.25rem' }}>{entry.label}</strong>
        <span style={{ display: 'block' }}>{entry.definition}</span>
        <span style={{ display: 'block', marginTop: '0.4rem' }}>
          <span style={{ color: 'var(--admin-text-muted, #64748b)', fontWeight: 600 }}>How it is computed: </span>
          {entry.computed}
        </span>
        <span style={{ display: 'block', marginTop: '0.4rem' }}>
          <span style={{ color: 'var(--admin-text-muted, #64748b)', fontWeight: 600 }}>If it looks wrong: </span>
          {entry.whenWrong}
        </span>
      </span>
    </span>
  );
}
